import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Hidden,
  Link,
  Tooltip,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/styles";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { getArmies } from "../../../../../components/api/APIUtilities";
import {
  BCPCalendarWeek,
  BCPClipboardList,
  BCPClock,
  BCPFlag,
  BCPHandshake,
  BCPLayerGroup,
  BCPListUl,
  BCPLocationDot,
  BCPSquarePlus,
  BCPUser,
  BCPUserGroup
} from "../../../../../components/icons/bcp/BCPIcons";
import InfoContainer from "../../../../../components/layout/InfoContainer";
import OrganizationCardSmall from "../../../../../components/layout/OrganizationCardSmall";
import UserCardSmall from "../../../../../components/layout/UserCardSmall";
import ListResponsive from "../../../../../components/other/ListResponsive";
import { AuthContext } from "../../../../../components/user/BCPAuthProvider";
import { postNotification } from "../../../../../components/utilities/Notifications";
import { getData } from "../../../../../components/utilities/UtilityFunctions";
import { BuildRecordJSX, userDisplayName } from "../../EventFunctions";
import OrganizePlayerModal from "../../OrganizePlayerModal";
import PlayerModal from "../../PlayerModal";

export default function EventInfoOverview(props) {
  const { event } = props;

  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventInfo"
  });

  const [descExpand, setDescExpand] = useState(false);

  const [player, setPlayer] = useState(null);

  const [placing, setPlacing] = useState(null);

  const [players, setPlayers] = useState([]);
  const [playerDict, setPlayerDict] = useState({});

  const [leaderBoards, setLeaderBoards] = useState([]);

  const [playersLoading, setPlayersLoading] = useState(true);
  const [leaderBoardsLoading, setLeaderBoardsLoading] = useState(true);

  const [armies, setArmies] = useState(null);
  const [refreshPlayers, setRefreshPlayers] = useState(false);

  const [refreshPlacings, setRefreshPlacings] = useState(false);

  const theme = useTheme();
  const mode = theme.palette.mode;
  const authContext = useContext(AuthContext);

  const cardSx = {
    p: 2,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start"
  };

  const cardSmallSx = {
    border: "",
    backgroundColor: "inherit",
    pl: 0,
    pr: 0,
    pt: 1,
    pb: 1,
    "&:hover": {
      backgroundColor:
        mode === "dark" ? "background.tertiary" : "background.secondary",
      transition: "0.2s"
    }
  };

  const fetchPlayers = async () => {
    let returnArray = [];
    try {
      const apiData = await getData(`/events/${event.id}/players`);
      returnArray = apiData?.active;

      if (!returnArray || !returnArray?.length) {
        return [];
      }

      returnArray.sort((a, b) => {
        var nameA = userDisplayName(
          a?.user?.firstName,
          a?.user?.lastName
        ).toLowerCase();
        var nameB = userDisplayName(
          b?.user?.firstName,
          b?.user?.lastName
        ).toLowerCase();
        if (nameA > nameB) return 1;
        if (nameA < nameB) return -1;
        return 0;
      });
    } catch (err) {
      console.error(err);
      return undefined;
    }
    return returnArray;
  };

  const fetchTeamPlayers = async () => {
    let returnArray = [];
    try {
      const apiData = await getData(`/events/${event.id}/teamplayers`);

      returnArray = apiData?.active;

      if (!returnArray || !returnArray?.length) {
        return [];
      }

      returnArray.sort((a, b) => {
        if (a.name?.toLowerCase() > b.name?.toLowerCase()) return 1;
        if (a.name?.toLowerCase() < b.name?.toLowerCase()) return -1;
        return 0;
      });
    } catch (err) {
      console.error(err);
      return undefined;
    }
    return returnArray;
  };

  async function parallel() {
    const taskPlayers = fetchPlayers();
    const taskTeamPlayers =
      event?.format?.teamEvent || event?.format?.doublesEvent
        ? fetchTeamPlayers()
        : [];

    return {
      players: await taskPlayers,
      teamPlayers: await taskTeamPlayers
    };
  }

  const handlePlayers = async () => {
    try {
      const parallelData = await parallel();

      if (
        (event?.format?.teamEvent || event?.format?.doublesEvent) &&
        parallelData.teamPlayers?.length &&
        parallelData.players?.length
      ) {
        var tempPlayerDict = {};
        parallelData.players.forEach((tempPlayer) => {
          tempPlayerDict[tempPlayer.id] = tempPlayer;
        });
        parallelData.teamPlayers.forEach((tempTeamPlayer) => {
          if (tempTeamPlayer.players?.length) {
            for (let i = 0; i < tempTeamPlayer.players.length; i++) {
              tempTeamPlayer.players[i] =
                tempPlayerDict[tempTeamPlayer.players[i].objectId];
            }
            tempTeamPlayer.players.sort((a, b) => {
              var nameA = userDisplayName(
                a?.firstName || a?.user?.firstName,
                a.lastName
              ).toLowerCase();
              var nameB = userDisplayName(
                b?.firstName || b?.user?.firstName,
                b?.lastName || b?.user?.lastName
              ).toLowerCase();
              if (nameA > nameB) return 1;
              if (nameA < nameB) return -1;
              return 0;
            });
          }
        });
        setPlayerDict(tempPlayerDict);
      }

      const players = parallelData.players || [];
      // const teamPlayers = parallelData.teamPlayers || [];

      handleLeaderBoards(players);
      setPlayers(players);
    } catch (err) {
      postNotification("error", err);
    } finally {
      setPlayersLoading(false);
    }
  };

  const fetchArmies = async () => {
    try {
      const apiArmies = await getArmies({
        gameType: event.gameSystem.code,
        "collections[]": ["subFactions"]
      });
      setArmies(apiArmies);
    } catch (err) {
      postNotification("error", err);
    }
  };

  const handleLeaderBoards = async () => {
    try {
      const playerArray = await getData(`/events/${event?.id}/players`, {
        placings: true
      });

      if (
        event.wcwEvent &&
        event.podRound >= (event?.status?.currentRound || 0)
      ) {
        playerArray?.active?.sort((a, b) => {
          if (a.podNum && !b.podNum) return -1;
          if (!a.podNum && b.podNum) return 1;
          if (a.podNum < b.podNum) return -1;
          if (a.podNum > b.podNum) return 1;
          for (let i = 0; i < a?.total_metrics?.length; i++) {
            if (a.total_metrics[i]?.value > b.total_metrics[i]?.value) {
              return -1;
            }
            if (a.total_metrics[i]?.value < b.total_metrics[i]?.value) {
              return 1;
            }
          }
          return 0;
        });
      } else {
        playerArray?.active?.sort((a, b) => {
          if (a.placing > b.placing) {
            return -1;
          }
          if (a.placing < b.placing) {
            return 1;
          }
          return 0;
        });
      }

      let placing = 0;

      playerArray?.active?.forEach((p) => {
        placing++;
        if (
          event.wcwEvent &&
          event.podRound >= (event.status?.currentRound || 0)
        ) {
          p.placing = placing;
        }

        p.overall1 = p.overall_metrics?.[0]?.value || 0;
        p.overall2 = p.overall_metrics?.[1]?.value || 0;
        p.overallBattlePoints = p.overall_metrics?.[0]?.value || 0;

        if (p.softScores) {
          var softScores = {};
          p.softScores.forEach((softScore) => {
            softScores[softScore.name] = softScore;
          });
          p.softScores = softScores;
        }
      });

      setLeaderBoards(playerArray?.active || []);
    } catch (err) {
      postNotification("error", err);
    } finally {
      setLeaderBoardsLoading(false);
    }
  };

  useEffect(() => {
    handlePlayers();

    if (event.gameSystem?.hasFactions) fetchArmies();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const check = async () => {
      try {
        if (!refreshPlayers) return;
        if (event) {
          setPlayersLoading(true);
          await handlePlayers();
        }
      } catch (err) {
        postNotification("error", err);
      }
      setRefreshPlayers(false);
    };
    check();
    // eslint-disable-next-line
  }, [refreshPlayers]);

  useEffect(() => {
    const check = async () => {
      try {
        if (!refreshPlacings) return;
        if (event) {
          setLeaderBoardsLoading(true);
          await handleLeaderBoards();
        }
      } catch (err) {
        postNotification("error", err);
      }
      setRefreshPlacings(false);
    };
    check();
    // eslint-disable-next-line
  }, [refreshPlacings]);

  return (
    <Grid container spacing={1}>
      {/* Card section */}
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent sx={cardSx}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ fontWeight: 700, mb: 2 }}
            >
              {t("eventOrganizer")}
            </Typography>

            {event?.gameStore?.id ? (
              <OrganizationCardSmall
                organization={event.gameStore}
                buttonSx={cardSmallSx}
              />
            ) : (
              <UserCardSmall user={event?.owner} buttonSx={cardSmallSx} />
            )}
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent sx={cardSx}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ fontWeight: 700, mb: 2 }}
            >
              {t("details")}
            </Typography>

            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography gutterBottom sx={{ display: "flex" }}>
                  <Box
                    sx={{
                      minWidth: "35px",
                      verticalAlign: "bottom",
                      display: "inline-flex"
                    }}
                  >
                    <Tooltip title={t("eventDate")}>
                      <BCPCalendarWeek />
                    </Tooltip>
                  </Box>
                  {event.location?.timezone
                    ? moment(event.dates?.start)
                        .tz(event.location.timezone)
                        .format("LL")
                    : moment(event.dates?.start).format("LL")}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography gutterBottom sx={{ display: "flex" }}>
                  <Box
                    sx={{
                      minWidth: "35px",
                      verticalAlign: "bottom",
                      display: "inline-flex"
                    }}
                  >
                    <Tooltip title={t("eventTime")}>
                      <BCPClock />
                    </Tooltip>
                  </Box>
                  {event.location?.timezone
                    ? `${moment(event.dates?.start)
                        .tz(event.location.timezone)
                        .format("LT")} - ${moment(event.dates?.end)
                        .tz(event.location.timezone)
                        .format("LT")}`
                    : `${moment(event.dates?.start).format("LT")} - ${moment(
                        event.dates?.end
                      ).format("LT")}`}
                </Typography>
              </Grid>

              {(event.isOnlineEvent ||
                Boolean(
                  event.location?.city ||
                    event.location?.zip ||
                    event.location?.state ||
                    event.location?.country
                )) && (
                <Grid item xs={12}>
                  <Typography gutterBottom sx={{ display: "flex" }}>
                    <Box
                      sx={{
                        minWidth: "35px",
                        verticalAlign: "bottom",
                        display: "inline-flex"
                      }}
                    >
                      <Tooltip title={t("eventLocation")}>
                        <BCPLocationDot />
                      </Tooltip>
                    </Box>
                    {event.isOnlineEvent
                      ? t("onlineEvent")
                      : event?.location?.formatted_address ||
                        `${
                          event.location?.streetNum ||
                          event.location?.streetName
                            ? `${event.location?.streetNum || ""} ${
                                event.location?.streetName || ""
                              }`.trim() + ", "
                            : ""
                        }${event?.location?.city ? `${event?.location?.city}, ` : ""}${event?.location?.state} ${
                          event.location?.country
                        }`.trim()}
                  </Typography>
                </Grid>
              )}
              {Boolean(event.eventFormat) && (
                <Grid item xs={12}>
                  <Typography gutterBottom>
                    <Box
                      sx={{
                        minWidth: "35px",
                        verticalAlign: "bottom",
                        display: "inline-flex"
                      }}
                    >
                      <Tooltip
                        title={
                          event?.gameSystem?.eventFormats?.label ||
                          t("eventFormat")
                        }
                      >
                        <BCPFlag />
                      </Tooltip>
                    </Box>
                    {event.eventFormat}
                  </Typography>
                </Grid>
              )}
              {event.status?.numberOfRounds &&
                event.status.numberOfRounds > 0 && (
                  <Grid item xs={12}>
                    <Typography gutterBottom>
                      <Box
                        sx={{
                          minWidth: "35px",
                          verticalAlign: "bottom",
                          display: "inline-flex"
                        }}
                      >
                        <Tooltip title={t("numberOfRounds")}>
                          <BCPLayerGroup />
                        </Tooltip>
                      </Box>
                      {event.status.numberOfRounds}
                      {t("rounds")}
                    </Typography>
                  </Grid>
                )}

              <Grid item xs={12}>
                <Typography gutterBottom sx={{ display: "flex" }}>
                  <Box
                    sx={{
                      minWidth: "35px",
                      verticalAlign: "bottom",
                      display: "inline-flex"
                    }}
                  >
                    <Tooltip title={t("numberOfPlayers")}>
                      <BCPUserGroup />
                    </Tooltip>
                  </Box>
                  {event?.countString} {event?.countLabel}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4} sx={{ mb: 1 }}>
        <Card>
          <CardContent
            sx={{
              ...cardSx,
              maxHeight: ["unset", "400px"],
              minHeight: "200px",
              overflowY: descExpand ? "scroll" : "hidden"
            }}
          >
            <Typography
              variant="h4"
              gutterBottom
              sx={{ fontWeight: 700, mb: 2 }}
            >
              {t("description")}
            </Typography>

            <Typography gutterBottom>
              {descExpand
                ? event?.description || t("noDescriptionFound")
                : trimDesc(event?.description, 300) ||
                  t("noDescriptionFound")}
            </Typography>

            {event?.description?.length > 300 && (
              <Button
                fullWidth
                onClick={(e) => {
                  setDescExpand(!descExpand);
                }}
                sx={{ color: "link.main", textAlign: "left" }}
              >
                {descExpand ? t("seeLess") : t("seeMore")}
              </Button>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={7}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
          {/* {event?.teamEvent ? "Teams" : "Players"} */}
          {t("players")}
        </Typography>
      </Grid>
      <Grid item xs={12} md={5}>
        <Button
          variant="contained"
          fullWidth
          href={`/organize/event/${event?.id}?active_tab=roster`}
          startIcon={<BCPUserGroup />}
        >
          {t("viewPlayers", {
            type: event?.format?.teamEvent ? t("teams") : t("players")
          })}
        </Button>
      </Grid>

      <Grid item xs={12} sx={{ mt: 2, mb: 2 }}>
        {!playersLoading && !players?.length ? (
          <InfoContainer
            title={t("noPlayersFoundTitle")}
            body={t("noPlayersFoundBody")}
            ActionButton={
              <Button
                variant="contained"
                href={`/organize/event/${event?.id}?active_tab=roster`}
                startIcon={<BCPSquarePlus />}
              >
                {t("addPlayers")}
              </Button>
            }
          />
        ) : (
          <ListResponsive
            name={t("playersList", {
              type: event?.format?.teamEvent ? t("teams") : t("players")
            })}
            originalList={playersLoading ? null : players}
            doPages
            initialRowsPerPage={5}
            rowsPerPageArray={[
              5,
              10,
              20,
              40,
              80,
              { label: t("all"), value: -1 }
            ]}
            centerNav
            rowProps={{
              headings: (
                <Grid item xs={12}>
                  <PlayerRow heading />
                </Grid>
              ),
              mapFunction: (row, index) => {
                return (
                  <Grid key={index} item xs={12}>
                    <PlayerRow
                      row={row}
                      setPlayer={setPlayer}
                      player={player}
                    />
                  </Grid>
                );
              }
            }}
          />
        )}
      </Grid>

      <Grid item xs={12} md={7}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
          {t("leaderboard")}
        </Typography>
      </Grid>
      <Grid item xs={12} md={5}>
        <Button
          variant="contained"
          fullWidth
          href={`/organize/event/${event?.id}?active_tab=placings`}
          startIcon={<BCPListUl />}
        >
          {t("viewLeaderboard")}
        </Button>
      </Grid>

      <Grid item xs={12} sx={{ mt: 2, mb: 2 }}>
        {!leaderBoardsLoading && !leaderBoards?.length ? (
          <InfoContainer
            title={t("noPlacingsFoundTitle")}
            body={t("noPlacingsFoundBody")}
            ActionButton={
              <Button
                variant="contained"
                href={`/organize/event/${event?.id}?active_tab=pairings`}
                startIcon={<BCPHandshake />}
              >
                {t("viewPairings")}
              </Button>
            }
          />
        ) : (
          <ListResponsive
            name={t("leaderboardList")}
            originalList={leaderBoardsLoading ? null : leaderBoards}
            doPages
            initialRowsPerPage={5}
            rowsPerPageArray={[
              5,
              10,
              20,
              40,
              80,
              { label: t("all"), value: -1 }
            ]}
            centerNav
            rowProps={{
              headings: (
                <Grid item xs={12}>
                  <PlayerPlacingRow heading />
                </Grid>
              ),
              mapFunction: (row, index) => {
                return (
                  <Grid key={index} item xs={12}>
                    <PlayerPlacingRow row={row} setPlacing={setPlacing} />
                  </Grid>
                );
              }
            }}
          />
        )}
      </Grid>

      {/* Hard-coded into singles players and singles placings for now */}
      <OrganizePlayerModal
        organize={true}
        player={player}
        setPlayer={setPlayer}
        playerDict={playerDict}
        event={event}
        armies={armies}
        setRefreshPlayers={setRefreshPlayers}
      />

      <PlayerModal
        isTeam={false}
        player={placing}
        setPlayer={setPlacing}
        playerDict={playerDict}
        event={event}
        authContext={authContext}
        organize={true}
        setRefreshPlayers={setRefreshPlacings}
        playerType={"player"}
      />
    </Grid>
  );
}

const PlayerRow = (props) => {
  const { heading, row, setPlayer } = props;

  const theme = useTheme();
  const mode = theme.palette.mode;
  const navigate = useNavigate();

  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventInfo"
  });

  const textSx = {
    textOverflow: "ellipsis",
    maxHeight: ["80px", "50px"],
    overflow: "hidden",
    whiteSpace: ["wrap", "nowrap"],
    pb: 1,
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
  };

  return (
    <Grid
      container
      spacing={1}
      sx={
        heading
          ? {
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: "4px 4px 0 0",
              backgroundColor: "table.headerBackground",
              color: "table.headerColor",
              p: 1
            }
          : {
              p: 1,
              fontSize: "16px",
              textAlign: "left",
              borderImage: [
                "linear-gradient(to right, rgba(0,0,0,0) 3%, black 3%, black 99%, rgba(0,0,0,0) 99%) 1;",
                "linear-gradient(to right, rgba(0,0,0,0) 1%, black 1%, black 99%, rgba(0,0,0,0) 99%) 1;"
              ],
              backgroundColor:
                mode === "dark" ? "#1e1e1e" : "background.secondary",
              cursor: "pointer"
            }
      }
      onClick={(e) => {
        if (setPlayer) setPlayer(row);
      }}
    >
      <Grid
        item
        xs
        sx={{
          ...textSx,
          maxWidth: ["180px", "500px"],
          fontWeight: heading ? 700 : 500
        }}
      >
        {heading ? (
          t("player")
        ) : (
          <>
            <Hidden smDown>
              <BCPUser size={18} style={{ "margin-right": "8px" }} />
            </Hidden>

            {userDisplayName(row?.user?.firstName, row?.user?.lastName)}
          </>
        )}
      </Grid>

      <Grid
        item
        xs
        sx={{
          ...textSx,
          maxWidth: ["125px", "unset"],
          fontWeight: heading ? 700 : 500
        }}
      >
        {heading ? (
          t("list")
        ) : (
          <Link
            sx={{
              textDecoration: "none",
              color: row?.listId ? "link.main" : "text.primary",
              cursor: row?.listId ? "pointer" : "default"
            }}
            onClick={(e) => {
              if (!row?.listId) {
                return;
              }

              navigate(
                row.listUrl !== undefined && row.listUrl !== ""
                  ? row.listUrl
                  : `/list/${row?.listId}`
              );
            }}
          >
            <Hidden smDown>
              <BCPClipboardList
                size={18}
                style={{
                  "margin-right": "8px",
                  color: row?.listId ? "" : theme.palette.warning.main
                }}
                outlined={Boolean(!row?.listId)}
              />
            </Hidden>
            {row?.faction?.name
              ? row.faction.name
              : row?.listId
                ? t("listSubmitted")
                : "-----"}
          </Link>
        )}
      </Grid>

      <Hidden mdDown>
        <Grid
          item
          xs
          sx={{
            ...textSx,
            maxWidth: ["125px", "unset"],
            fontWeight: heading ? 700 : 500
          }}
        >
          {heading ? t("team") : row?.team?.name || "-----"}
        </Grid>
      </Hidden>

      <Grid
        item
        xs
        sx={{
          ...textSx,
          maxWidth: ["80px", "130px"],
          fontWeight: heading ? 700 : 500
        }}
      >
        {heading ? (
          t("status")
        ) : (
          <Typography
            variant="body1"
            sx={{
              color: row?.dropped
                ? "error.main"
                : row?.checkedIn
                  ? "success.main"
                  : "warning.main",
              fontWeight: 700
            }}
          >
            {row?.dropped
              ? t("dropped")
              : row?.checkedIn
                ? t("checkedIn")
                : t("notCheckedIn")}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};

const PlayerPlacingRow = (props) => {
  const { heading, row, setPlacing } = props;

  const theme = useTheme();
  const mode = theme.palette.mode;
  const navigate = useNavigate();

  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventInfo"
  });

  const textSx = {
    textOverflow: "ellipsis",
    maxHeight: ["80px", "50px"],
    overflow: "hidden",
    whiteSpace: ["wrap", "nowrap"],
    pb: 1,
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
  };

  return (
    <Grid
      container
      spacing={1}
      sx={
        heading
          ? {
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: "4px 4px 0 0",
              backgroundColor: "table.headerBackground",
              color: "table.headerColor",
              p: 1
            }
          : {
              p: 1,
              fontSize: "16px",
              textAlign: "left",
              borderImage: [
                "linear-gradient(to right, rgba(0,0,0,0) 3%, black 3%, black 99%, rgba(0,0,0,0) 99%) 1;",
                "linear-gradient(to right, rgba(0,0,0,0) 1%, black 1%, black 99%, rgba(0,0,0,0) 99%) 1;"
              ],
              backgroundColor:
                mode === "dark" ? "#1e1e1e" : "background.secondary",
              cursor: "pointer"
            }
      }
      onClick={(e) => {
        if (setPlacing) setPlacing(row);
      }}
    >
      <Grid
        item
        xs
        sx={{
          ...textSx,
          maxWidth: ["70px", "90px"],
          fontWeight: heading ? 700 : 500,
          color:
            row?.manualPlacing || row?.manuallyPlaced
              ? "warning.main"
              : "text.primary"
        }}
      >
        {heading ? t("placing") : row?.placing || "-"}
      </Grid>
      <Grid
        item
        xs
        sx={{
          ...textSx,
          maxWidth: ["180px", "500px"],
          fontWeight: heading ? 700 : 500
        }}
      >
        {heading ? (
          "Player"
        ) : (
          <>
            <Hidden smDown>
              <BCPUser size={16} style={{ "margin-right": "8px" }} />
            </Hidden>

            {userDisplayName(row?.user?.firstName, row?.user?.lastName)}
          </>
        )}
      </Grid>

      <Grid
        item
        xs
        sx={{
          ...textSx,
          maxWidth: ["125px", "unset"],
          fontWeight: heading ? 700 : 500
        }}
      >
        {heading ? (
          t("list")
        ) : (
          <Link
            sx={{
              textDecoration: "none",
              color: row?.listId ? "link.main" : "text.primary",
              cursor: row?.listId ? "pointer" : "default"
            }}
            onClick={(e) => {
              if (!row?.listId) {
                return;
              }

              navigate(
                row.listUrl !== undefined && row.listUrl !== ""
                  ? row.listUrl
                  : `/list/${row?.listId}`
              );
            }}
          >
            <Hidden smDown>
              <BCPClipboardList
                size={18}
                style={{
                  "margin-right": "8px",
                  color: row?.listId ? "" : theme.palette.warning.main
                }}
                outlined={Boolean(!row?.listId)}
              />
            </Hidden>
            {row?.faction?.name
              ? row.faction.name
              : row?.listId
                ? t("listSubmitted")
                : "-----"}
          </Link>
        )}
      </Grid>

      <Hidden mdDown>
        <Grid
          item
          xs
          sx={{
            ...textSx,
            maxWidth: ["125px", "unset"],
            fontWeight: heading ? 700 : 500
          }}
        >
          {heading
            ? t("record")
            : row?.games?.length || row?.pod_games?.length
              ? BuildRecordJSX({ player: row }) || "-----"
              : "-----"}
        </Grid>
      </Hidden>

      <Hidden mdDown>
        <Grid
          item
          xs
          sx={{
            ...textSx,
            maxWidth: ["80px", "130px"],
            fontWeight: heading ? 700 : 500
          }}
        >
          {heading ? (
            t("points")
          ) : (
            <Typography variant="body1">
              {row?.overall1 ? row.overall1 : "-----"}
            </Typography>
          )}
        </Grid>
      </Hidden>
    </Grid>
  );
};

function trimDesc(str, length = 300) {
  if (!str || str === "") return "";

  if (str?.length > length) {
    return str.substring(0, length) + "...";
  }
  return str;
}
